import type { CSSProperties, ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { isEmpty } from "lodash";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { getBlocksFromChildren } from "@circle-react-shared/BuilderUI/Helpers/blockUtils";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";

interface GridProps extends Omit<ComponentPropsWithRef<"div">, "style"> {
  children: ReactNode;
  styles?: Readonly<Record<string, StyleValue>>;
}

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({ children, styles = {}, className, ...rest }, ref) => {
    const inlineStyles: CSSProperties = {
      ...parseStyles({
        styles: omit(styles, ["sizing", "gridType", "columns"]),
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
      gridTemplateColumns:
        styles.gridType?.value === "filled"
          ? "repeat(auto-fill, minmax(200px, 1fr))"
          : `repeat(${Number(styles.columns?.value) || defaultStyles.columns}, 1fr)`,
      gridTemplateRows: "auto",
    };

    const childrenBlocks = getBlocksFromChildren(children);

    return (
      <div
        ref={ref}
        className={classNames(
          "wb-w-full wb-h-auto wb-min-h-24 wb-p-5 wb-grid wb-auto-rows-auto wb-bg-primary wb-border-primary wb-gap-5",
          {
            "wb-border-gray-500 wb-border wb-border-dashed wb-shadow-[inset_0_0_2px_4px_rgba(0,0,0,0.1),inset_0_0_2px_4px_rgba(255,255,255,0.2),inset_0_0_3px_rgba(255,255,255,0.3)]":
              isEmpty(childrenBlocks),
          },
          className,
        )}
        style={inlineStyles}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Grid.displayName = "Grid";
